import { createTheme, responsiveFontSizes, adaptV4Theme } from "@mui/material";

// -----------------------------------------------------------------------
// Palette — warm editorial light mode.
// Cream paper background, near-black ink for text, a single muted clay
// accent used sparingly for links / CTAs / highlights.
// -----------------------------------------------------------------------
const paper = "#FAF7F2";
const ink = "#1C1A17";
const inkSoft = "#5B564E";
const clay = "#B5502E";
const clayDark = "#8F3D22";
const clayLight = "#D98E6F";
const line = "rgba(28, 26, 23, 0.12)";
const darkSection = "#23201B";

const warningLight = "rgba(181, 80, 46, .12)";
const warningMain = "rgba(181, 80, 46, .22)";
const warningDark = "rgba(181, 80, 46, .4)";

// border
const borderWidth = 1;
const borderColor = line;

// breakpoints
const xl = 1920;
const lg = 1280;
const md = 960;
const sm = 600;
const xs = 0;

// spacing
const spacing = 8;

// -----------------------------------------------------------------------
// Typography — Fraunces (serif display, slight editorial character) for
// headings, Inter for everything functional.
// -----------------------------------------------------------------------
const serif = "'Fraunces', Georgia, 'Times New Roman', serif";
const sans = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const theme = createTheme(adaptV4Theme({
  palette: {
    mode: "light",
    primary: { main: ink, light: inkSoft, dark: "#000000", contrastText: paper },
    secondary: { main: clay, light: clayLight, dark: clayDark, contrastText: paper },
    common: {
      black: ink,
      darkBlack: darkSection,
      white: paper,
      ink,
      inkSoft,
      clay,
      clayDark,
      clayLight,
      paper,
      line,
    },
    text: {
      primary: ink,
      secondary: inkSoft,
    },
    warning: {
      light: warningLight,
      main: warningMain,
      dark: warningDark
    },
    tonalOffset: 0.2,
    background: {
      default: paper,
      paper: "#FFFFFF"
    },
    divider: line,
    spacing
  },
  breakpoints: {
    values: {
      xl,
      lg,
      md,
      sm,
      xs
    }
  },
  border: {
    borderColor: borderColor,
    borderWidth: borderWidth
  },
  shape: {
    borderRadius: 2
  },
  shadows: [
    "none",
    "0 1px 2px rgba(28,26,23,0.06)",
    "0 2px 6px rgba(28,26,23,0.07)",
    "0 3px 10px rgba(28,26,23,0.08)",
    "0 4px 14px rgba(28,26,23,0.08)",
    "0 6px 18px rgba(28,26,23,0.09)",
    "0 8px 22px rgba(28,26,23,0.09)",
    "0 8px 22px rgba(28,26,23,0.09)",
    "0 10px 26px rgba(28,26,23,0.1)",
    "0 10px 26px rgba(28,26,23,0.1)",
    "0 12px 30px rgba(28,26,23,0.1)",
    "0 12px 30px rgba(28,26,23,0.1)",
    "0 14px 34px rgba(28,26,23,0.11)",
    "0 14px 34px rgba(28,26,23,0.11)",
    "0 16px 38px rgba(28,26,23,0.11)",
    "0 16px 38px rgba(28,26,23,0.11)",
    "0 18px 42px rgba(28,26,23,0.12)",
    "0 18px 42px rgba(28,26,23,0.12)",
    "0 20px 46px rgba(28,26,23,0.12)",
    "0 20px 46px rgba(28,26,23,0.12)",
    "0 22px 50px rgba(28,26,23,0.13)",
    "0 22px 50px rgba(28,26,23,0.13)",
    "0 24px 54px rgba(28,26,23,0.13)",
    "0 24px 54px rgba(28,26,23,0.13)",
    "0 26px 58px rgba(28,26,23,0.14)"
  ],
  overrides: {
    MuiCssBaseline: {
      "@global": {
        html: { scrollBehavior: "smooth" },
        body: { backgroundColor: paper }
      }
    },
    MuiButton: {
      root: {
        textTransform: "none",
        borderRadius: 2,
        fontWeight: 500,
        letterSpacing: "0.01em"
      },
      contained: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "none"
        }
      },
      containedPrimary: {
        backgroundColor: ink,
        "&:hover": {
          backgroundColor: "#000000"
        }
      }
    },
    MuiCard: {
      root: {
        borderRadius: 2,
        border: `1px solid ${line}`,
        boxShadow: "none"
      }
    },
    MuiExpansionPanel: {
      root: {
        position: "static"
      }
    },
    MuiTableCell: {
      root: {
        paddingLeft: spacing * 2,
        paddingRight: spacing * 2,
        borderBottom: `${borderWidth}px solid ${borderColor}`,
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing
        }
      }
    },
    MuiDivider: {
      root: {
        backgroundColor: borderColor,
        height: borderWidth
      }
    },
    MuiPrivateNotchedOutline: {
      root: {
        borderWidth: borderWidth
      }
    },
    MuiListItem: {
      divider: {
        borderBottom: `${borderWidth}px solid ${borderColor}`
      }
    },
    MuiDialog: {
      paper: {
        width: "100%",
        maxWidth: 430,
        marginLeft: spacing,
        marginRight: spacing
      }
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: ink,
        fontFamily: sans
      }
    },
    MuiExpansionPanelDetails: {
      root: {
        [`@media (max-width:  ${sm}px)`]: {
          paddingLeft: spacing,
          paddingRight: spacing
        }
      }
    }
  },
  typography: {
    useNextVariants: true,
    fontFamily: sans,
    h1: {
      fontFamily: serif,
      fontWeight: 500,
      letterSpacing: "-0.02em",
      lineHeight: 1.05
    },
    h2: {
      fontFamily: serif,
      fontWeight: 500,
      letterSpacing: "-0.015em",
      lineHeight: 1.1
    },
    h3: {
      fontFamily: serif,
      fontWeight: 500,
      letterSpacing: "-0.01em",
      lineHeight: 1.15
    },
    h4: {
      fontFamily: serif,
      fontWeight: 500,
      letterSpacing: "-0.01em",
      lineHeight: 1.2
    },
    h5: {
      fontFamily: serif,
      fontWeight: 500,
      lineHeight: 1.25
    },
    h6: {
      fontFamily: sans,
      fontWeight: 600,
      letterSpacing: "0.01em"
    },
    subtitle1: {
      fontFamily: sans,
      fontWeight: 400,
      color: inkSoft
    },
    subtitle2: {
      fontFamily: sans,
      fontWeight: 600,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      fontSize: "0.78rem",
      color: inkSoft
    },
    body1: {
      fontFamily: sans,
      lineHeight: 1.7
    },
    body2: {
      fontFamily: sans,
      lineHeight: 1.6
    },
    overline: {
      fontFamily: sans,
      fontWeight: 500,
      letterSpacing: "0.08em"
    },
    button: {
      fontFamily: sans
    }
  }
}));

export default responsiveFontSizes(theme);
